import React from 'react'

import {
  useAppConfig,
  EmptyState,
  Loader,
  useBudgetsStore,
  useCategoryStore,
  useGlobalCopyStore,
  useScreenSize,
  useWidgetContainerProvider,
} from '../../common'

interface BudgetsEmptyStateProps {
  isMiniWidget?: boolean
  onConnectAccountsClick: () => void
  createBudgetOnClick: () => void
}
export const BudgetsEmptyState: React.FC<BudgetsEmptyStateProps> = ({
  isMiniWidget = false,
  onConnectAccountsClick,
  createBudgetOnClick,
}) => {
  const { config } = useAppConfig()
  const { recalculateBudgets } = useBudgetsStore()
  const { spendCategories } = useCategoryStore()
  const { budgets: copy } = useGlobalCopyStore()
  const { availableWidth } = useWidgetContainerProvider()
  const { isMobile } = useScreenSize(availableWidth)

  const isCompact = isMiniWidget && isMobile

  const [isError, setError] = React.useState(false)
  const [shouldShowEmptyGeneratedBudgets, setShouldShowEmptyGeneratedBudgets] =
    React.useState(false)
  const [recalculateBudgetsLoading, setRecalculateBudgetsLoading] = React.useState(false)

  const newBudgetCategories = React.useMemo(() => {
    return [...spendCategories.filter((category) => category.totalAverageAmount > 0)]
  }, [spendCategories])

  const autoGenerateBudgets = async () => {
    setRecalculateBudgetsLoading(true)
    const { data, isSuccess } = await recalculateBudgets(newBudgetCategories)
    setRecalculateBudgetsLoading(false)
    if (isSuccess) {
      if (data.length === 0) {
        setShouldShowEmptyGeneratedBudgets(true)
      }
      setError(false)
    } else {
      setError(true)
    }
  }

  if (recalculateBudgetsLoading) {
    return <Loader />
  }
  if (isError) {
    return (
      <EmptyState
        header={copy.autogenerate_budgets_error_header}
        icon={'error'}
        iconColor="#4D4D4D"
        onClick={(buttonType) => {
          if (buttonType === 'primary') {
            autoGenerateBudgets()
          } else {
            createBudgetOnClick()
          }
        }}
        primaryButton={copy.autogenerate_budgets_error_primary_button}
        secondaryButton={copy.zero_state_generate_budgets_secondary_button}
        subText={copy.autogenerate_budgets_error_subheader}
        sx={{
          width: { sm: 468 },
          '.buttons-footer': {
            width: 208,
            ...(isCompact && { mt: 16, button: { height: 'max-content' } }),
          },
          ...(isCompact && { justifyContent: 'center', mt: 0, svg: { height: 32, width: 32 } }),
        }}
      />
    )
  }

  return (
    <>
      {shouldShowEmptyGeneratedBudgets ? (
        <EmptyState
          header={copy.zero_state_generate_budgets_header}
          icon="error"
          iconColor="#4D4D4D"
          onClick={(buttonType: string) => {
            if (buttonType === 'primary') {
              onConnectAccountsClick()
            } else {
              createBudgetOnClick()
            }
          }}
          primaryButton={
            config.show_connections_widget_in_master
              ? copy.zero_state_generate_budgets_primary_button
              : undefined
          }
          secondaryButton={copy.zero_state_generate_budgets_secondary_button}
          subText={copy.zero_state_generate_budgets_subheader}
          sx={{
            width: isMiniWidget ? '100%' : { sm: 500 },
            height: isMiniWidget ? '100%' : { sm: 468 },
            mt: isCompact ? 0 : 50,
            '.buttons-footer': {
              width: 208,
              height: 100,
              ...(isCompact && { mt: 16, button: { height: 'max-content' } }),
            },
            padding: isMiniWidget ? 5 : 0,
            ...(isCompact && { justifyContent: 'center', svg: { height: 32, width: 32 } }),
          }}
        />
      ) : (
        <EmptyState
          header={copy.create_budgets_title}
          icon="bubble_chart"
          iconColor="#1A1A1A"
          onClick={(buttonType: string) => {
            if (buttonType === 'primary') {
              autoGenerateBudgets()
            } else {
              createBudgetOnClick()
            }
          }}
          primaryButton={copy.empty_state_primary_button}
          secondaryButton={!isMiniWidget ? copy.empty_state_secondary_button : undefined}
          subText={copy.empty_state_subheader}
          sx={{
            width: isMiniWidget ? '100%' : { sm: 432 },
            height: isMiniWidget ? '100%' : { sm: 468 },
            mt: isCompact ? 0 : 50,
            '.buttons-footer': {
              width: 208,
              height: 100,
              ...(isCompact && { mt: 24, button: { height: 'max-content' } }),
            },
            padding: isMiniWidget ? 4 : 0,
            ...(isCompact && { justifyContent: 'center', svg: { height: 32, width: 32 } }),
          }}
        />
      )}
    </>
  )
}
